import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  IconButton,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  Lock,
  LockOpen,
  WhatsApp,
} from "@mui/icons-material";
import { useAuth } from "../../contexts";
import { validatePassword } from "../../utils/passwordUtils";

const SecuritySection: React.FC = () => {
  const {
    client,
    hasPassword,
    createPassword,
    updatePassword,
    removePassword,
  } = useAuth();

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState<"success" | "error" | "info">(
    "info"
  );
  const [showRemoveDialog, setShowRemoveDialog] = useState(false);

  const clearForm = () => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setMessage("");
  };

  const handleCreatePassword = async () => {
    const validation = validatePassword(newPassword);
    if (!validation.isValid) {
      setMessage(validation.errors.join(", "));
      setMessageType("error");
      return;
    }

    if (newPassword !== confirmPassword) {
      setMessage("As senhas não coincidem");
      setMessageType("error");
      return;
    }

    setLoading(true);
    try {
      const success = await createPassword(newPassword);
      if (success) {
        setMessage("Senha criada com sucesso!");
        setMessageType("success");
        clearForm();
      }
    } catch (error) {
      setMessage("Erro ao criar senha");
      setMessageType("error");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePassword = async () => {
    if (!currentPassword.trim()) {
      setMessage("Digite a senha atual");
      setMessageType("error");
      return;
    }

    const validation = validatePassword(newPassword);
    if (!validation.isValid) {
      setMessage(validation.errors.join(", "));
      setMessageType("error");
      return;
    }

    if (newPassword !== confirmPassword) {
      setMessage("As senhas não coincidem");
      setMessageType("error");
      return;
    }

    setLoading(true);
    try {
      const success = await updatePassword(currentPassword, newPassword);
      if (success) {
        setMessage("Senha atualizada com sucesso!");
        setMessageType("success");
        clearForm();
      }
    } catch (error) {
      setMessage("Erro ao atualizar senha");
      setMessageType("error");
    } finally {
      setLoading(false);
    }
  };

  const handleRemovePassword = async () => {
    if (!currentPassword.trim()) {
      setShowRemoveDialog(false);
      setMessage("Digite a senha atual");
      setMessageType("error");
      return;
    }

    setLoading(true);
    try {
      const success = await removePassword(currentPassword);
      if (success) {
        setMessage("Senha removida com sucesso!");
        setMessageType("success");
        clearForm();
        setShowRemoveDialog(false);
      }
    } catch (error) {
      setMessage("Erro ao remover senha");
      setMessageType("error");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = () => {
    const message = `Olá, gostaria de recuperar a senha do usuário ${client.name}`;
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/5551998368882?text=${encodedMessage}`;
    window.open(whatsappUrl, "_blank");
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        {hasPassword ? <Lock color="primary" /> : <LockOpen color="primary" />}
        <Typography variant="subtitle1">Segurança da Conta</Typography>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {hasPassword
          ? "Sua conta está protegida por uma senha. Você pode alterar ou remover a senha abaixo."
          : "Proteja sua conta criando uma senha. Isso ajudará a manter seus agendamentos seguros."}
      </Typography>

      {message && (
        <Alert severity={messageType} sx={{ mb: 2 }}>
          {message}
        </Alert>
      )}

      {hasPassword ? (
        // Cliente tem senha - mostrar opções de alterar/remover
        <Box>
          <TextField
            label="Senha atual"
            type={showCurrentPassword ? "text" : "password"}
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            fullWidth
            size="small"
            margin="normal"
            disabled={loading}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    edge="end"
                  >
                    {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            label="Nova senha"
            type={showNewPassword ? "text" : "password"}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            fullWidth
            size="small"
            margin="normal"
            disabled={loading}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    edge="end"
                  >
                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            label="Confirmar nova senha"
            type={showConfirmPassword ? "text" : "password"}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            fullWidth
            size="small"
            margin="normal"
            disabled={loading}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
            <Button
              variant="contained"
              onClick={handleUpdatePassword}
              disabled={
                loading || !currentPassword || !newPassword || !confirmPassword
              }
            >
              {loading ? "Atualizando..." : "Atualizar Senha"}
            </Button>

            <Button
              variant="outlined"
              color="error"
              onClick={() => setShowRemoveDialog(true)}
              disabled={loading}
            >
              Remover Senha
            </Button>
          </Box>

          <Box sx={{ mt: 2 }}>
            <Button
              variant="text"
              color="primary"
              onClick={handleForgotPassword}
              startIcon={<WhatsApp />}
              sx={{ textTransform: "none" }}
            >
              Esqueci minha senha
            </Button>
          </Box>
        </Box>
      ) : (
        // Cliente não tem senha - mostrar opção de criar
        <Box>
          <TextField
            label="Nova senha"
            type={showNewPassword ? "text" : "password"}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            fullWidth
            size="small"
            margin="normal"
            disabled={loading}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    edge="end"
                  >
                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            label="Confirmar nova senha"
            type={showConfirmPassword ? "text" : "password"}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            fullWidth
            size="small"
            margin="normal"
            disabled={loading}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              onClick={handleCreatePassword}
              disabled={loading || !newPassword || !confirmPassword}
            >
              {loading ? "Criando..." : "Criar Senha"}
            </Button>
          </Box>
        </Box>
      )}

      {/* Dialog de confirmação para remover senha */}
      <Dialog
        open={showRemoveDialog}
        onClose={() => setShowRemoveDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Remover Senha</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja remover a senha da sua conta? Isso tornará
            sua conta menos segura.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowRemoveDialog(false)}>Cancelar</Button>
          <Button
            onClick={handleRemovePassword}
            color="error"
            variant="contained"
            disabled={loading}
          >
            {loading ? "Removendo..." : "Remover Senha"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SecuritySection;
